/** Service Desk console: Installations board, list and project detail (C09/C10, SD-S3-10). */
export const serviceInstallations = {
  subtitle: "Fit-out and commissioning {{projectsLc}}, {{stageLc}} by {{stageLc}}, with the {{items}} and the client acceptance that starts the warranty.",
  orgTab: { emptyTitle: "No {{areaLc}}", emptyBody: "{{projects}} for new equipment appear here.", meta: "Lead {{lead}} · {{passed}} of {{total}} passed" },
  // The neutral words, used until the workspace vocabulary is known and where it names no plural.
  words: { project: "Project", projects: "Projects", stage: "Stage", stages: "Stages", openItems: "Open items", area: "Installations" },
  status: {
    planned: "Planned",
    in_progress: "In progress",
    punch_list: "Closing items",
    ready_for_acceptance: "Ready for acceptance",
    handed_over: "Handed over",
    on_hold: "On hold",
    cancelled: "Cancelled",
  },
  actions: {
    create: "New {{project}}",
    edit: "Edit {{projectLc}}",
    start: "Start {{projectLc}}",
    startDone: "{{number}} started",
    hold: "Put on hold",
    resume: "Resume",
    resumeDone: "{{number}} resumed",
    cancel: "Cancel {{projectLc}}",
  },
  stats: {
    label: "Summary",
    active: "Active",
    activeHint: "in progress or planned",
    punch: "{{items}}",
    punchHint: "{{projectsLc}} closing items",
    ready: "Ready for acceptance",
    readyHint: "waiting for the client",
    handedOver: "Handed over",
    handedOverHint: "accepted by the client",
  },
  filters: {
    view: "View",
    board: "Board",
    list: "List",
    search: "Search number or name",
    searchLabel: "Search {{projectsLc}}",
    lead: "Lead",
    allLeads: "All leads",
    statusGroup: "{{project}} status",
    chip: { all: "All", active: "Active", handed_over: "Handed over" },
  },
  board: {
    label: "{{projects}} by status",
    column: { planned: "Planned", in_progress: "In progress", punch_list: "Closing items", ready_for_acceptance: "Ready for acceptance", handed_over: "Handed over", paused: "On hold or cancelled" },
    empty: "No {{projectsLc}}",
    open: "Open {{number}} {{name}}",
    stages: "{{passed}} of {{total}} passed",
    progressLabel: "{{passed}} of {{total}} {{stagesLc}} passed",
    items: "{{done}}/{{total}} {{items}}",
    noItems: "No {{items}}",
    late: "Past planned end",
  },
  list: {
    label: "{{area}}",
    organisation: "Organisation",
    lead: "Lead",
    plannedEnd: "Planned end",
    status: "Status",
    verified: "{{done}}/{{total}} verified",
  },
  empty: {
    title: "No {{areaLc}} yet",
    body: "Start a {{projectLc}} to track its {{stagesLc}}, the {{items}} and the client acceptance.",
    filteredTitle: "No {{projectsLc}} match",
    filteredBody: "Clear a filter to see every {{projectLc}}.",
  },
  error: { title: "{{area}} couldn't load", body: "Check your connection and try again. Nothing was changed." },
  errors: {
    notReady: "The {{projectLc}} is not ready for acceptance yet: a {{stageLc}} has not passed or an item is still open.",
    closed: "This {{projectLc}} is closed and can no longer be changed.",
  },
  form: {
    createTitle: "New {{project}}",
    createDescription: "{{stages}} come from the template. A {{stageLc}} can only start when the one before it has passed.",
    editTitle: "Edit {{number}}",
    name: "{{project}} name",
    organisation: "Organisation",
    chooseOrganisation: "Choose an organisation",
    template: "Template",
    templateHint: "The {{stagesLc}} the {{projectLc}} goes through.",
    defaultTemplate: "Workspace default",
    site: "Site",
    noSite: "No site",
    lead: "Lead",
    noLead: "No lead",
    startOn: "Starts",
    endOn: "Planned end",
    warrantyMonths: "Warranty (months)",
    warrantyHint: "Counted from the client's acceptance. Leave empty to use each asset's own.",
    assets: "Assets in this {{projectLc}}",
    assetsHint: "Existing assets of the organisation. More can be added later.",
    description: "Description",
    notes: "Notes",
    create: "Create {{project}}",
    save: "Save changes",
    created: "{{number}} created",
    updated: "{{number}} saved",
  },
  validation: {
    organisation: "Choose the organisation this {{projectLc}} is for.",
    name: "Give the {{projectLc}} a name of up to {{max}} characters.",
    dates: "The planned end cannot be before the start.",
    months: "Enter a whole number of months from 1 to {{max}}.",
    text: "At most {{max}} characters.",
  },
  detail: {
    back: "{{area}}",
    loading: "Loading the {{projectLc}}",
    more: "More actions",
    notFoundTitle: "{{project}} not found",
    notFoundBody: "It may have been archived. Go back to the list to find it.",
    tbc: "TBC",
    leadAndDates: "Lead {{lead}} · {{start}} to {{end}}",
    noLead: "not set",
    statusReason: "Reason: {{reason}}",
  },
  actionDialog: {
    reason: "Reason",
    needsReason: "Say why. The reason is kept on the {{projectLc}}.",
    holdTitle: "Put {{number}} on hold?",
    holdBody: "Work stops until you resume it. Nothing is lost.",
    holdConfirm: "Put on hold",
    holdDone: "{{number}} is on hold",
    cancelTitle: "Cancel {{number}}?",
    cancelBody: "A cancelled {{projectLc}} cannot be reopened. Its assets and documents are kept.",
    cancelConfirm: "Cancel {{projectLc}}",
    cancelDone: "{{number}} cancelled",
  },
  stage: {
    title: "{{stage}} progress",
    order: "Order is enforced: a {{stageLc}} starts only when the one before it has passed.",
    stepLabel: "{{n}}. {{label}}: {{status}}",
    status: { pending: "Pending", in_progress: "In progress", passed: "Passed", failed: "Failed" },
    parallel: "Can start at any time",
    optional: "Optional",
    started: "Started {{date}}",
    finished: "Finished {{date}}",
    onAcceptance: "This {{stageLc}} is passed by the client's acceptance.",
    checklist_one: "Checklist: {{open}} to do",
    checklist_other: "Checklist: {{open}} to do",
    action: { start: "Start {{stageLc}}", pass: "Mark {{label}} passed", fail: "Mark failed", retest: "Retest", reopen: "Reopen" },
    moved: { pending: "{{label}} is pending again", in_progress: "{{label}} started", passed: "{{label}} passed", failed: "{{label}} failed" },
    blocked: {
      previous_not_passed: "Blocked: the {{stageLc}} before it has not passed.",
      later_stage_started: "Blocked: a later {{stageLc}} has already started.",
      checklist_incomplete: "Blocked: finish the checklist first.",
      not_allowed: "This {{stageLc}} cannot move that way.",
    },
  },
  punch: {
    status: { open: "Open", in_progress: "In progress", fixed: "Awaiting verification", verified: "Verified", reopened: "Reopened", waived: "Waived" },
    filters: {
      group: "Item status",
      all: "All",
      open: "Open",
      in_progress: "In progress",
      fixed: "Awaiting verification",
      verified: "Verified",
      reopened: "Reopened",
      waived: "Waived",
      search: "Search item or location",
      searchLabel: "Search items",
      assignee: "Assignee",
      allAssignees: "All assignees",
    },
    table: {
      label: "Items",
      selectAll: "Select all items",
      select: "Select {{number}}",
      item: "Item",
      location: "Location",
      asset: "Asset",
      assignee: "Assignee",
      status: "Status",
      open: "Open {{number}} {{title}}",
      via: "via",
      nonBlocking: "Not blocking",
    },
    emptyTitle: "No {{items}} yet",
    emptyBody: "Items raised by the team or by the client show up here.",
    emptyFilteredTitle: "No items match",
    emptyFilteredBody: "Clear the filter to see every item.",
    errorTitle: "The items couldn't load",
    errorBody: "Check your connection and try again.",
    refused: {
      evidence_required: "Add an after photo or a note before marking this item fixed.",
      reason_required: "A reason is required.",
      not_allowed: "That move is not allowed from the item's current status.",
      not_for_actor: "Only the {{projectLc}} team can do that.",
    },
    add: {
      open: "Add item",
      title: "Add to {{item}}",
      description: "A new item for {{number}}. The client sees it in the portal.",
      titleLabel: "Title",
      location: "Location",
      asset: "Asset",
      noAsset: "None",
      assignee: "Assignee",
      unassigned: "Unassigned",
      dueOn: "Due on",
      descriptionLabel: "Description",
      blocking: "Keeps the {{projectLc}} from being ready",
      blockingHint: "The item must be verified or waived before the {{projectLc}} can be accepted.",
      save: "Add item",
      added: "{{number}} added",
      problems: { title: "Describe the item in up to {{max}} characters." },
    },
    move: {
      action: { start: "Start work", fixed: "Mark fixed", verify: "Verify on site", waive: "Waive", reopen: "Reopen" },
      title: { fixed: "Mark fixed", verify: "Verify on site", waive: "Waive item", reopen: "Reopen item" },
      reason: { verify: "Reason", waive: "Reason for waiving", reopen: "Reason" },
      note: "Note",
      notePlaceholder: "What was done?",
      evidenceHint: "A note or an after photo is required to mark an item fixed.",
      save: "Save",
      problems: { reason: "Give a reason of at least {{min}} characters.", evidence: "Add an after photo or a note before marking this item fixed." },
      done: { start: "{{number}} in progress", fixed: "{{number}} marked fixed", verify: "{{number}} verified", waive: "{{number}} waived", reopen: "{{number}} reopened" },
    },
    bulk: {
      title: { verify: "Verify selected items on site", waive: "Waive selected items", reassign: "Reassign selected items" },
      description_one: "{{count}} item selected, {{eligible}} can be changed.",
      description_other: "{{count}} items selected, {{eligible}} can be changed.",
      action: { verify: "Verify on site", waive: "Waive", reassign: "Reassign" },
      verifyHint: "Only items that are fixed can be verified; the others are skipped.",
      reason: "Reason",
      assignTo: "Assign to",
      choose: "Choose a person",
      apply: "Apply",
      problems: { assignee: "Choose who the items go to." },
      done: { verify: "{{done}} of {{total}} items verified", waive: "{{done}} of {{total}} items waived", reassign: "{{done}} of {{total}} items reassigned" },
    },
    drawer: {
      notFoundTitle: "Item not found",
      notFoundBody: "It may have been removed from the list.",
      awaiting: "Fixed, awaiting verification by the client or by the {{projectLc}} lead with the reason \"verified on site\".",
      waived: "Waived: {{reason}}",
      verified: "Verified: {{reason}}",
      location: "Location",
      asset: "Asset",
      raisedBy: "Raised by",
      actor: { staff: "The team", contact: "The client" },
      ticket: "Source ticket",
      assignee: "Assignee",
      dueOn: "Due on",
      blocking: "Blocks acceptance",
      blockingYes: "Yes",
      blockingNo: "No",
      reopened: "Times reopened",
      fixNote: "What was done",
      photos: "Photos",
      update: "Update",
      readOnly: "Only the {{projectLc}} team can change an item. This {{projectLc}} is read-only now.",
      previous: "Previous",
      next: "Next",
    },
    photos: {
      before: "Before",
      after: "After",
      none: "No {{phase}} photo",
      add: "Add {{phase}} photo",
      added: "Photo added",
      remove: "Remove {{name}}",
      unavailable: "Photo unavailable",
    },
  },
  acceptance: {
    title: "Acceptance",
    chip: { blocked: "Blocked", ready: "Ready for acceptance", awaiting: "Waiting for the client", accepted: "Handed over", paused: "On hold", cancelled: "Cancelled" },
    headline: {
      blocked: "Not ready for acceptance.",
      ready: "Ready for acceptance.",
      awaiting: "Waiting for the client.",
      accepted: "Accepted and signed.",
      paused: "On hold.",
      cancelled: "Cancelled.",
    },
    body: {
      blocked: "Close the items below first.",
      ready: "Every item is verified or waived and every {{stageLc}} has passed. Mark the {{projectLc}} ready so the client can sign.",
      awaiting: "The client's organisation admin can now sign in the portal.",
      accepted: "The warranty has started on each asset.",
      paused: "Resume the {{projectLc}} to continue.",
      cancelled: "A cancelled {{projectLc}} cannot be accepted.",
    },
    requirements: "What is still needed",
    itemsDone: "All {{items}} verified or waived",
    toGo_one: "{{count}} to go",
    toGo_other: "{{count}} to go",
    stagesPassed: "Every {{stage}} passed",
    notPassed: "{{names}} not passed",
    who: "The client's organisation admin signs after a one-time code. Acceptance starts the warranty on each asset.",
    markReady: "Mark ready for acceptance",
    markedReady: "{{number}} is ready for the client to accept",
    notReadyToast: "Not ready yet: close the items and {{stagesLc}} listed above.",
    certificate: "Certificate of acceptance",
    preview: "Preview certificate",
    certificateSaved: "Certificate saved as PDF",
    previewSaved: "Preview saved as PDF. The final certificate is made when the client signs.",
    signedBy: "Signed by",
    signedAt: "Signed",
    comments: "Comments",
    noRecord: "The acceptance record is not available.",
  },
  assets: {
    title: "Assets in this {{project}}",
    asset: "Asset",
    accepted: "Accepted",
    awaiting: "Awaiting acceptance",
    unlink: "Remove {{tag}} from this {{projectLc}}",
    pick: "Add an asset",
    pickPlaceholder: "Choose an asset to add",
    link: "Add asset",
    linked: "Asset added to the {{projectLc}}",
    emptyTitle: "No assets registered yet",
    emptyBody: "Assets appear here once the equipment is delivered and tagged.",
  },
  documents: {
    addTitle: "Add a {{projectLc}} document",
    kinds: { report: "Report", drawing: "Drawing", manual: "Manual", certificate: "Certificate", photo: "Photo", other: "Other" },
    emptyTitle: "No documents yet",
    emptyBody: "{{stage}} reports, as-built drawings and manuals go here.",
  },
} as const;
